import { call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { extractErrorMessage } from '../../../utils/apiResponseHandler';
import { Error } from '../../actions/error/error';
import { getURLWithParams } from '../../../utils/url';
import {
  LIST_REQUEST,    LIST_RESPONSE,    LIST_FAILURE,
  DETAIL_REQUEST,  DETAIL_RESPONSE,  DETAIL_FAILURE,
  PREVIEW_REQUEST, PREVIEW_RESPONSE, PREVIEW_FAILURE,

  CREATE_REQUEST,  CREATE_SUCCESS,   CREATE_FAILURE,
  UPDATE_REQUEST,  UPDATE_SUCCESS,   UPDATE_FAILURE,
  DELETE_REQUEST,  DELETE_SUCCESS,   DELETE_FAILURE,

  SUBSCRIBE_REQUEST, SUBSCRIBE_SUCCESS, SUBSCRIBE_FAILURE,
  CANCEL_REQUEST,    CANCEL_SUCCESS,    CANCEL_FAILURE,
  SUBSCRIPTIONS_REQUEST, SUBSCRIPTIONS_RESPONSE, SUBSCRIPTIONS_FAILURE,

  MY_LISTINGS_REQUEST, MY_LISTINGS_RESPONSE, MY_LISTINGS_FAILURE,
  EARNINGS_REQUEST,    EARNINGS_RESPONSE,    EARNINGS_FAILURE,

  PUBLISHED_SIGNALS_REQUEST, PUBLISHED_SIGNALS_RESPONSE, PUBLISHED_SIGNALS_FAILURE,
  SELLER_STATUS_REQUEST,  SELLER_STATUS_RESPONSE,  SELLER_STATUS_FAILURE,
  PROVIDER_APPLY_REQUEST, PROVIDER_APPLY_SUCCESS,  PROVIDER_APPLY_FAILURE,

  fetchMyListings,
  fetchSubscriptions,
  fetchSellerStatus as refetchSellerStatus,
  fetchPublishedSignals as refetchPublishedSignals,
} from '../../actions/marketplace/listings';

const unwrap = (response) => response.data?.data ?? response.data;
const errMsg = extractErrorMessage;

// ---- discover -----------------------------------------------------------

function* listListings({ payload }) {
  try {
    const url = getURLWithParams(
      `${config.API_URL}/marketplace/listings`,
      payload?.filters || {},
    );
    const response = yield call(axios.get, url, { headers: getLocalUser() });
    yield put({ type: LIST_RESPONSE, payload: unwrap(response) });
  } catch (error) {
    yield put(Error(error));
    yield put({ type: LIST_FAILURE, payload: errMsg(error) });
  }
}

function* getListing({ payload }) {
  try {
    const { listingId } = payload;
    const response = yield call(
      axios.get,
      `${config.API_URL}/marketplace/listings/${listingId}`,
      { headers: getLocalUser() },
    );
    yield put({ type: DETAIL_RESPONSE, payload: unwrap(response) });
  } catch (error) {
    yield put(Error(error));
    yield put({ type: DETAIL_FAILURE, payload: errMsg(error) });
  }
}

function* getPreview({ payload }) {
  try {
    const { listingId, limit } = payload;
    const response = yield call(
      axios.get,
      `${config.API_URL}/marketplace/listings/${listingId}/preview?limit=${limit || 100}`,
      { headers: getLocalUser() },
    );
    yield put({ type: PREVIEW_RESPONSE, payload: unwrap(response) });
  } catch (error) {
    yield put(Error(error));
    yield put({ type: PREVIEW_FAILURE, payload: errMsg(error) });
  }
}

// ---- sell-side mutations ------------------------------------------------

function* createListing({ payload }) {
  try {
    const response = yield call(
      axios.post,
      `${config.API_URL}/marketplace/listings`,
      payload?.body || {},
      { headers: getLocalUser() },
    );
    yield put({ type: CREATE_SUCCESS, payload: unwrap(response) });
    yield put(fetchMyListings());
    // Refresh the per-workspace published-signal lookup so the
    // Data Signals page immediately swaps the "Put on Marketplace"
    // affordance for the green "Manage on marketplace" indicator
    // without a manual page reload.
    yield put(refetchPublishedSignals());
  } catch (error) {
    yield put(Error(error));
    yield put({ type: CREATE_FAILURE, payload: errMsg(error) });
  }
}

function* updateListing({ payload }) {
  try {
    const { listingId, body } = payload;
    const response = yield call(
      axios.patch,
      `${config.API_URL}/marketplace/listings/${listingId}`,
      body || {},
      { headers: getLocalUser() },
    );
    yield put({ type: UPDATE_SUCCESS, payload: unwrap(response) });
    yield put(fetchMyListings());
    yield put(refetchPublishedSignals());
  } catch (error) {
    yield put(Error(error));
    yield put({ type: UPDATE_FAILURE, payload: errMsg(error) });
  }
}

function* deleteListing({ payload }) {
  try {
    const { listingId, force } = payload;
    const url =
      `${config.API_URL}/marketplace/listings/${listingId}`
      + (force ? '?force=true' : '');
    const response = yield call(axios.delete, url, {
      headers: getLocalUser(),
    });
    yield put({ type: DELETE_SUCCESS, payload: unwrap(response) });
    yield put(fetchMyListings());
    yield put(refetchPublishedSignals());
  } catch (error) {
    // The 409 active-subscribers branch carries the listing
    // metadata the seller needs to make a cascade decision; we
    // forward the full error payload so any opener (manage-published
    // dialog, monetization page) can render its own confirm flow.
    // BE wraps payload as ``{ data: <payload> }`` (responseJSON in
    // core/src/utils/api_utils.py); unwrap defensively.
    const status = error?.response?.status;
    const raw = error?.response?.data || {};
    const body = raw?.data && typeof raw.data === 'object' ? raw.data : raw;
    // Canonical envelope: {error: {code, message, details}}.
    const errCode = typeof body?.error === 'object' ? body.error?.code : body?.error;
    if (status === 409 && errCode === 'listing_has_active_subscribers') {
      const details = (typeof body?.error === 'object' && body.error?.details) || body;
      yield put({
        type: DELETE_FAILURE,
        payload: {
          conflict:           true,
          listingId:          payload?.listingId,
          active_subscribers: details.active_subscribers,
        },
      });
      return;
    }
    yield put(Error(error));
    yield put({ type: DELETE_FAILURE, payload: errMsg(error) });
  }
}

// ---- buy-side -----------------------------------------------------------

function* subscribe({ payload }) {
  try {
    const { listingId } = payload;
    const response = yield call(
      axios.post,
      `${config.API_URL}/marketplace/listings/${listingId}/subscribe`,
      {},
      { headers: getLocalUser() },
    );
    yield put({ type: SUBSCRIBE_SUCCESS, payload: unwrap(response) });
    yield put(fetchSubscriptions());
  } catch (error) {
    yield put(Error(error));
    yield put({ type: SUBSCRIBE_FAILURE, payload: errMsg(error) });
  }
}

function* cancelSub({ payload }) {
  try {
    const { subscriptionId } = payload;
    const response = yield call(
      axios.post,
      `${config.API_URL}/marketplace/subscriptions/${subscriptionId}/cancel`,
      {},
      { headers: getLocalUser() },
    );
    yield put({ type: CANCEL_SUCCESS, payload: unwrap(response) });
    yield put(fetchSubscriptions());
  } catch (error) {
    yield put(Error(error));
    yield put({ type: CANCEL_FAILURE, payload: errMsg(error) });
  }
}

function* listSubscriptions() {
  try {
    const response = yield call(
      axios.get,
      `${config.API_URL}/marketplace/subscriptions`,
      { headers: getLocalUser() },
    );
    yield put({ type: SUBSCRIPTIONS_RESPONSE, payload: unwrap(response) });
  } catch (error) {
    yield put(Error(error));
    yield put({ type: SUBSCRIPTIONS_FAILURE, payload: errMsg(error) });
  }
}

// ---- seller dashboards --------------------------------------------------

function* myListings() {
  try {
    const response = yield call(
      axios.get,
      `${config.API_URL}/marketplace/my-listings`,
      { headers: getLocalUser() },
    );
    yield put({ type: MY_LISTINGS_RESPONSE, payload: unwrap(response) });
  } catch (error) {
    yield put(Error(error));
    yield put({ type: MY_LISTINGS_FAILURE, payload: errMsg(error) });
  }
}

function* earnings() {
  try {
    const response = yield call(
      axios.get,
      `${config.API_URL}/marketplace/earnings`,
      { headers: getLocalUser() },
    );
    yield put({ type: EARNINGS_RESPONSE, payload: unwrap(response) });
  } catch (error) {
    yield put(Error(error));
    yield put({ type: EARNINGS_FAILURE, payload: errMsg(error) });
  }
}

function* publishedSignals() {
  try {
    const response = yield call(
      axios.get,
      `${config.API_URL}/marketplace/published-signals`,
      { headers: getLocalUser() },
    );
    yield put({ type: PUBLISHED_SIGNALS_RESPONSE, payload: unwrap(response) });
  } catch (error) {
    yield put(Error(error));
    yield put({ type: PUBLISHED_SIGNALS_FAILURE, payload: errMsg(error) });
  }
}

function* sellerStatus() {
  try {
    const response = yield call(
      axios.get,
      `${config.API_URL}/marketplace/seller-status`,
      { headers: getLocalUser() },
    );
    yield put({ type: SELLER_STATUS_RESPONSE, payload: unwrap(response) });
  } catch (error) {
    // Silent failure: a missing status just means the publish
    // affordances stay hidden. No global error toast.
    yield put({ type: SELLER_STATUS_FAILURE, payload: errMsg(error) });
  }
}

function* providerApply({ payload }) {
  try {
    const response = yield call(
      axios.post,
      `${config.API_URL}/marketplace/provider/apply`,
      payload?.body || {},
      { headers: getLocalUser() },
    );
    yield put({ type: PROVIDER_APPLY_SUCCESS, payload: unwrap(response) });
    yield put(refetchSellerStatus());
  } catch (error) {
    yield put(Error(error));
    yield put({ type: PROVIDER_APPLY_FAILURE, payload: errMsg(error) });
  }
}

export function* watch() {
  yield takeLatest(LIST_REQUEST,    listListings);
  yield takeLatest(DETAIL_REQUEST,  getListing);
  yield takeLatest(PREVIEW_REQUEST, getPreview);
  yield takeLatest(CREATE_REQUEST,  createListing);
  yield takeLatest(UPDATE_REQUEST,  updateListing);
  yield takeLatest(DELETE_REQUEST,  deleteListing);
  yield takeLatest(SUBSCRIBE_REQUEST, subscribe);
  yield takeLatest(CANCEL_REQUEST,    cancelSub);
  yield takeLatest(SUBSCRIPTIONS_REQUEST, listSubscriptions);
  yield takeLatest(MY_LISTINGS_REQUEST, myListings);
  yield takeLatest(EARNINGS_REQUEST,    earnings);
  yield takeLatest(PUBLISHED_SIGNALS_REQUEST, publishedSignals);
  yield takeLatest(SELLER_STATUS_REQUEST,  sellerStatus);
  yield takeLatest(PROVIDER_APPLY_REQUEST, providerApply);
}
